/** @file Moves direction-fix animation keys (bases) after an animation rename; counts colliding keys dropped. */
import type { Draft } from 'immer';
import type { Doc } from '@pose2d/format/types';
import { mixNames } from '@pose2d/format/doc/mixNames';
import { names } from '@pose2d/format/names';

/** Moves one fix key `oldBase` to `newBase` (an existing `newBase` key wins); true when that collision dropped it. */
function moveFixKey(fixes: Record<string, string> | undefined, oldBase: string, newBase: string): boolean {
  const animId = fixes?.[oldBase];
  if (!fixes || animId === undefined) return false;
  const clash = Object.hasOwn(fixes, newBase);
  if (!clash) fixes[newBase] = animId;
  Reflect.deleteProperty(fixes, oldBase);
  return clash;
}

/** Call after the rename: moves the fix keys of `from`'s base to `to`'s base; returns the colliding keys dropped. */
export function renameFixBases(draft: Draft<Doc>, from: string, to: string): number {
  // W2-31: in every `directions.entries.*.fix.animations`, the key equal to the old @DIR base moves only once no
  // animation keeps that base (the same rule as renameMixSides); the value (the fix animation id) is kept.
  const entries = draft.directions?.entries;
  const oldBase = names.splitAnimation(from).base;
  const newBase = names.splitAnimation(to).base;
  if (!entries || oldBase === newBase || mixNames(draft.animations).has(oldBase)) return 0;
  return names.DIRS.filter((dir) => moveFixKey(entries[dir]?.fix?.animations, oldBase, newBase)).length;
}
